import * as THREE from 'three';
import { Socket } from 'socket.io-client';
import { CityGenerator } from './CityGenerator';
import { LabelManager2D } from './LabelManager2D';

// Interface pour les données reçues du serveur
interface PatrolData {
  id: string;
  startPosition: { i: number; j: number };
  targetPosition: { i: number; j: number };
  startTime: number;
  currentTime: number;
  direction: string;
}

// Interface pour les données de patrol côté client
interface ClientPatrolData {
  id: string;
  model: THREE.Group;
  startPosition: { i: number; j: number };
  targetPosition: { i: number; j: number };
  startTime: number;
  direction: string;
  lastUpdateTime: number;
}

export class PolicePatrolController {
  private scene: THREE.Scene;
  private socket: Socket;
  private cityGenerator: CityGenerator;
  private patrols: Map<string, ClientPatrolData> = new Map();
  private readonly PATROL_MOVEMENT_DURATION: number = 900; // Ms pour se déplacer d'une intersection à l'autre (identique au serveur)
  private isGridInitialized: boolean = false;
  private lastUpdateTime: number = 0;
  private labelManager: LabelManager2D | null = null;

  constructor(scene: THREE.Scene, socket: Socket, cityGenerator: CityGenerator) {
    this.scene = scene;
    this.socket = socket;
    this.cityGenerator = cityGenerator;
    this.lastUpdateTime = performance.now();
    
    // Écouter l'événement cityGrid pour savoir quand la grille est initialisée
    this.socket.on('cityGrid', () => {
      setTimeout(() => {
        // Attendre un peu que la grille soit complètement chargée
        this.isGridInitialized = true;
        console.log("Grid initialized for police patrols");
      }, 500);
    });
    
    // Écouter les événements liés aux patrouilleurs
    this.setupSocketListeners();
  }
  
  private setupSocketListeners(): void {
    // Écouter l'événement d'initialisation des patrouilleurs
    this.socket.on('initializePatrols', (patrols: PatrolData[]) => {
      console.log("Initializing police patrols:", patrols);
      patrols.forEach(patrol => {
        this.createPatrol(patrol);
      });
    });
    
    // Écouter les mises à jour de position des patrouilleurs
    this.socket.on('updatePatrolPosition', (data: PatrolData) => {
      this.updatePatrol(data);
    });
    
    // Écouter la suppression d'un patrouilleur
    this.socket.on('removePatrol', (patrolId: string) => {
      this.removePatrol(patrolId);
    });
  }
  
  public setLabelManager(labelManager: LabelManager2D): void {
    this.labelManager = labelManager;
  }
  
  private createPatrol(data: PatrolData): void {
    const patrolId = data.id;
    
    // Vérifier si la patrouille existe déjà
    if (this.patrols.has(patrolId)) {
      console.warn(`Patrol ${patrolId} already exists`);
      return;
    }
    
    // Créer le modèle 3D de la patrouille
    const patrolModel = new THREE.Group();
    
    // Charger le modèle FBX de la voiture de police qui existe dans les assets
    const assetPath = 'cartooncity_141_fbx/Cars/City_Car_Police.fbx';
    const position = new THREE.Vector3(0, 0.1, 0); // Ajusté pour être légèrement au-dessus du sol
    const rotation = new THREE.Euler(0, 0, 0);
    const scale = new THREE.Vector3(0.335, 0.335, 0.335); // Reduce scale by 33% (from 0.5 to 0.335)
    
    console.log(`Creating police patrol ${patrolId} with scale ${scale}`);
    this.cityGenerator.loadModelAsset(assetPath, position, rotation, scale, patrolModel, null);
    
    // Calculer la position 3D initiale à partir des indices de grille
    const worldPosition = this.gridToWorldPosition(data.startPosition.i, data.startPosition.j);
    patrolModel.position.copy(worldPosition);
    
    // Calculer la rotation initiale à partir de la direction
    const directionVector = this.directionToVector(data.direction);
    patrolModel.rotation.y = Math.atan2(directionVector.x, directionVector.z);
    
    // Ajouter un indicateur visuel pour la police (gyrophare simplifié)
    const lightGroup = new THREE.Group();
    lightGroup.position.set(0, 0.8, 0); // Adjust height from 1.2 to 0.8 (33% smaller)
    patrolModel.add(lightGroup);
    
    // Créer les lumières rouge et bleue du gyrophare
    const redLightGeometry = new THREE.BoxGeometry(0.1, 0.04, 0.1); // Reduce sizes by 33%
    const redLightMaterial = new THREE.MeshPhongMaterial({ color: 0xff0000, emissive: 0xff0000, emissiveIntensity: 0.5 });
    const redLight = new THREE.Mesh(redLightGeometry, redLightMaterial);
    redLight.position.set(-0.054, 0, 0); // Reduce offset by 33% (from -0.08 to -0.054)
    lightGroup.add(redLight);
    
    const blueLightGeometry = new THREE.BoxGeometry(0.1, 0.04, 0.1); // Reduce sizes by 33%
    const blueLightMaterial = new THREE.MeshPhongMaterial({ color: 0x0000ff, emissive: 0x0000ff, emissiveIntensity: 0.5 });
    const blueLight = new THREE.Mesh(blueLightGeometry, blueLightMaterial);
    blueLight.position.set(0.054, 0, 0); // Reduce offset by 33% (from 0.08 to 0.054)
    lightGroup.add(blueLight);
    
    // Créer l'objet ClientPatrolData
    const clientPatrol: ClientPatrolData = {
      id: patrolId,
      model: patrolModel,
      startPosition: data.startPosition,
      targetPosition: data.targetPosition,
      startTime: data.startTime,
      direction: data.direction,
      lastUpdateTime: data.currentTime
    };
    
    // Ajouter la patrouille à la scène et à la liste des patrouilles
    this.scene.add(patrolModel);
    this.patrols.set(patrolId, clientPatrol);
    
    // Créer un label pour la patrouille
    if (this.labelManager) {
      const labelPosition = new THREE.Vector3(
        worldPosition.x,
        worldPosition.y + 1.0, // Reduced from 1.5 to 1.0 (33% smaller)
        worldPosition.z
      );
      this.labelManager.createLabel(patrolId, "Police", labelPosition);
    }
    
    console.log(`Added patrol ${patrolId} at position`, worldPosition);
  }
  
  private updatePatrol(data: PatrolData): void {
    const patrolId = data.id;
    let patrol = this.patrols.get(patrolId);
    
    // Si la patrouille n'existe pas encore, la créer
    if (!patrol) {
      console.log(`Creating missing patrol ${patrolId}`);
      this.createPatrol(data);
      return;
    }
    
    // Mettre à jour les données de la patrouille
    patrol.startPosition = data.startPosition;
    patrol.targetPosition = data.targetPosition;
    patrol.startTime = data.startTime;
    patrol.direction = data.direction;
    patrol.lastUpdateTime = data.currentTime;
    
    // Interpoler la position n'est pas nécessaire ici car cela sera fait dans la méthode update()
    // à chaque frame d'animation
    
    // Animer les lumières du gyrophare
    const lightGroup = patrol.model.children.find(child => child instanceof THREE.Group) as THREE.Group;
    if (lightGroup && lightGroup.children.length >= 2) {
      const redLight = lightGroup.children[0] as THREE.Mesh;
      const blueLight = lightGroup.children[1] as THREE.Mesh;
      
      if (redLight.material instanceof THREE.MeshPhongMaterial) {
        redLight.material.emissiveIntensity = Math.sin(Date.now() * 0.01) * 0.5 + 0.5;
      }
      if (blueLight.material instanceof THREE.MeshPhongMaterial) {
        blueLight.material.emissiveIntensity = Math.sin(Date.now() * 0.01 + Math.PI) * 0.5 + 0.5;
      }
    }
  }
  
  private removePatrol(patrolId: string): void {
    const patrol = this.patrols.get(patrolId);
    
    if (!patrol) {
      console.warn(`Cannot remove patrol ${patrolId}: patrol not found`);
      return;
    }
    
    // Supprimer le patrouilleur de la scène
    this.scene.remove(patrol.model);
    
    // Supprimer le label
    if (this.labelManager) {
      this.labelManager.removeLabel(patrolId);
    }
    
    // Supprimer le patrouilleur de la Map
    this.patrols.delete(patrolId);
    
    console.log(`Police patrol ${patrolId} removed from scene`);
  }
  
  private checkCollisionWithPlayer(patrolId: string, model: THREE.Group): void {
    // Récupérer la position du joueur depuis le serveur
    this.socket.emit('getPlayerPosition', (playerPosition: THREE.Vector3 | null) => {
      if (!playerPosition) {
        return; // Pas de joueur connecté
      }
      
      // Calculer la distance entre le patrouilleur et le joueur
      const distance = model.position.distanceTo(playerPosition);
      
      // Si la distance est inférieure à un seuil, considérer qu'il y a collision
      const collisionThreshold = 2.0; // Ajuster selon les besoins
      
      if (distance < collisionThreshold) {
        console.log(`Collision detected between player and patrol ${patrolId}`);
        
        // Informer le serveur de la collision
        this.socket.emit('patrolCollision', { patrolId });
      }
    });
  }
  
  public update(): void {
    // Calcul du delta time pour un mouvement cohérent
    const currentTime = performance.now();
    const deltaTime = (currentTime - this.lastUpdateTime) / 1000; // Convertir en secondes
    this.lastUpdateTime = currentTime;
    
    // Mettre à jour chaque patrouille
    this.patrols.forEach((patrol) => {
      // Calculer le temps écoulé depuis le début du mouvement
      const elapsedTime = Date.now() - patrol.startTime;
      
      // Calculer le ratio d'interpolation (0 à 1)
      const ratio = Math.min(1, elapsedTime / this.PATROL_MOVEMENT_DURATION);
      
      // Interpoler entre la position de départ et la position cible
      const startWorldPos = this.gridToWorldPosition(patrol.startPosition.i, patrol.startPosition.j);
      const targetWorldPos = this.gridToWorldPosition(patrol.targetPosition.i, patrol.targetPosition.j);
      
      // Interpolation linéaire entre les deux positions
      const interpolatedPosition = new THREE.Vector3(
        startWorldPos.x + (targetWorldPos.x - startWorldPos.x) * ratio,
        startWorldPos.y, // La hauteur reste constante
        startWorldPos.z + (targetWorldPos.z - startWorldPos.z) * ratio
      );
      
      // Appliquer la position interpolée
      patrol.model.position.copy(interpolatedPosition);
      
      // Calculer la direction de mouvement pour la rotation
      if (ratio < 1) { // Si en mouvement
        const directionVector = new THREE.Vector3(
          targetWorldPos.x - startWorldPos.x,
          0,
          targetWorldPos.z - startWorldPos.z
        ).normalize();
        
        // Calculer la rotation à partir de la direction
        patrol.model.rotation.y = Math.atan2(directionVector.x, directionVector.z);
      }
      
      // Mettre à jour la position du label
      if (this.labelManager) {
        const labelPosition = new THREE.Vector3(
          patrol.model.position.x,
          patrol.model.position.y + 1.0, // Reduced from 1.5 to 1.0 (33% smaller)
          patrol.model.position.z
        );
        this.labelManager.updateLabel(patrol.id, labelPosition);
      }
      
      // Vérifier les collisions avec le joueur
      this.checkCollisionWithPlayer(patrol.id, patrol.model);
    });
  }
  
  // Convertir les indices de grille en position 3D
  private gridToWorldPosition(i: number, j: number): THREE.Vector3 {
    const position = this.cityGenerator.calculatePosition(i, j);
    return new THREE.Vector3(
      position.x + this.cityGenerator.getRoadWidth() / 2, 
      -0.1, 
      position.z + this.cityGenerator.getRoadWidth() / 2);
  }
  
  // Convertir une direction textuelle en vecteur
  private directionToVector(direction: string): THREE.Vector3 {
    switch (direction) {
      case 'north':
        return new THREE.Vector3(0, 0, -1);
      case 'south':
        return new THREE.Vector3(0, 0, 1);
      case 'east':
        return new THREE.Vector3(1, 0, 0);
      case 'west':
        return new THREE.Vector3(-1, 0, 0);
      default:
        return new THREE.Vector3(0, 0, 0);
    }
  }
  
  public getPatrols(): Map<string, ClientPatrolData> {
    return this.patrols;
  }
} 